// cover — dark full-bleed hero (v15 slide 1). Title serif over the photograph, the
// «{client-logo} × NAVEN» lockup (logo from the clients record, name as text fallback),
// mono subtitle band at the foot.
import type { CoverBound, CoverContent } from '../../../../shared/deckTypes.js';
import { Abs, Dkp, FramedImg, PageNo } from './chrome.js';

export default function CoverPreview({ content, bound, pageNo }: {
  content: CoverContent;
  bound?: CoverBound;
  pageNo?: number | null;
}) {
  const c = content;
  const logoUrl = bound?.has_logo ? `/api/clients/${bound.client_id}/logo` : null;
  return (
    <Dkp variant="photo">
      {c.hero_image
        ? (
          <div className="dkp-bg" style={{ overflow: 'hidden' }}>
            <FramedImg src={c.hero_image} content={c as Record<string, unknown>} slotKey="hero_image" />
          </div>
        )
        : <div className="dkp-bg" style={{ background: '#0B0C0E' }} />}
      <div className="dkp-scrim" />

      {/* hero title — serif ~64px, at most 2 lines of ~24 characters (v15: y191, centred) */}
      <Abs x={90} y={168} w={1100} style={{ textAlign: 'center' }}>
        <div className="dkp-serif" style={{
          fontSize: 64, lineHeight: 1.05, color: '#F3F1EC',
          textShadow: '0 2px 22px rgba(11,12,14,.35)',
        }}>
          {c.title}
          {c.title ? <span style={{ color: '#C8A96E' }}> ●</span> : null}
        </div>
      </Abs>

      {/* the lockup «{client} × NAVEN●» (v15 y348.9): client logo from the record when
          uploaded (height 63px like the LCH mark), client_name in type as fallback;
          '×' in mono, NAVEN in Geist Black (weight 900) with the smaller gold dot. */}
      <Abs x={0} y={340} w={1280} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24,
      }}>
        {logoUrl ? (
          <img src={logoUrl} alt="" style={{
            height: 63, maxWidth: 300, objectFit: 'contain',
            filter: 'drop-shadow(0 1px 6px rgba(11,12,14,.35))',
          }} />
        ) : c.client_name ? (
          <span style={{ fontSize: 42.7, fontWeight: 700, color: '#F3F1EC', letterSpacing: '-.01em' }}>
            {c.client_name}
          </span>
        ) : null}
        {(logoUrl || c.client_name) ? (
          <span className="dkp-mono" style={{ fontSize: 42.7, color: '#F3F1EC' }}>×</span>
        ) : null}
        <span style={{ fontSize: 42.7, fontWeight: 900, color: '#F3F1EC', letterSpacing: '-.005em' }}>
          NAVEN<span style={{ color: '#C8A96E', fontSize: 20 }}>●</span>
        </span>
      </Abs>

      {/* subtitle (v15: y475, centred, 20px) */}
      {c.subtitle ? (
        <Abs x={240} y={478} w={800} style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 20, fontWeight: 500, color: '#FFFFFF',
            textShadow: '0 1px 12px rgba(11,12,14,.45)',
          }}>{c.subtitle}</div>
        </Abs>
      ) : null}

      {/* mono date/meta band (v15: y640, centred) */}
      {c.date_label ? (
        <Abs x={140} y={640} w={1000} style={{ textAlign: 'center' }}>
          <div className="dkp-mono" style={{
            fontSize: 10.7, letterSpacing: '.14em', textTransform: 'uppercase', color: '#D9D6CF',
          }}>{c.date_label}</div>
        </Abs>
      ) : null}

      <PageNo n={pageNo} light />
    </Dkp>
  );
}
